import React, { useEffect, useMemo, useState } from 'react'
import injectStyles from 'react-jss'
import moment from 'moment'
import { Select } from '@material-ui/core'
import { MenuItem } from 'material-ui'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import { styles } from './styles'
import GenericFlatButton from '../../../../../../../../Components/GenericFlatButton'
import {
  CONTRACT_TYPES_STRING,
  GERMANY_CONTRACT_ADDENDUM_TYPES,
  GERMANY_CONTRACT_STATUSES,
} from '../../../../../../../../shared/constants'
import DropZoneArea from '../../../../../../../Campaigns/Page/Codes/components/Popups/CodeUploadPopups/DropZoneArea'
import { addendumTypeOptions, hoursPerWeekOptions } from '../../const'
import {
  getDefaultEndDate,
  getStartsOfCurrentAndNextMonth,
  validateEndDate,
} from '../../utils'
import TriangleAlert from '../../../../../../../../Assets/Images/icons/TriangleAlert'
import { getBase64 } from '../../../../../../../StatsDeliverability/UploadFile/utils'

const formInitial = {
  type: null,
  hoursPerWeek: null,
  hoursAffectingFrom: null,
  endDate: null,
}

const CreateAddendumModal = ({
  contract,
  contractName,
  onCancel,
  onCreate,
  refetchAmbassadorData,
  classes,
  isDatesInRangeContract,
}) => {
  const [type, setType] = useState(null)
  const [form, setForm] = useState(formInitial)
  const [file, setFile] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  const [viewType, setViewType] = useState('edit')
  const [isEndDateEnabled, setIsEndDateEnabled] = useState(true)
  const [fileError, setFileError] = useState(null)

  const typeWithDisabledEndDate = [
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SHOW_UP,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SEND_LETTER,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_LESS_THAN_6_MONTH,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_MORE_THAN_6_MONTH,
  ]

  useEffect(() => {
    const date = getDefaultEndDate({
      type,
      isDatesInRangeContract,
      contractEndAt: contract.endAt,
    })
    if (typeWithDisabledEndDate.includes(type)) {
      setIsEndDateEnabled(false)
    } else {
      setIsEndDateEnabled(true)
    }
    if (date) {
      handleFormChange('endDate', date)
      validateEndDate({
        endDate: date,
        contract,
        setErrors: setValidationErrors,
        type,
        isDatesInRangeContract,
      })
    } else {
      handleFormChange('endDate', null)
    }
  }, [type])

  const handleFormChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const handleTypeChange = type => {
    setForm(formInitial)
    setFile(null)
    setType(type)
  }

  const itsHoursChangeFlow =
    type == GERMANY_CONTRACT_ADDENDUM_TYPES.CHANGE_AMOUNT_HOURS
  const itsEndTimeChangingFlow = [
    GERMANY_CONTRACT_ADDENDUM_TYPES.CONTRACT_EXTENSION,
    GERMANY_CONTRACT_ADDENDUM_TYPES.IMMEDIATE_RESIGNATION,
    GERMANY_CONTRACT_ADDENDUM_TYPES.FUTURE_RESIGNATION,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SHOW_UP,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SEND_LETTER,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_LESS_THAN_6_MONTH,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_MORE_THAN_6_MONTH,
  ].includes(type)

  const canShowAttachmentBlock = [
    GERMANY_CONTRACT_ADDENDUM_TYPES.IMMEDIATE_RESIGNATION,
    GERMANY_CONTRACT_ADDENDUM_TYPES.FUTURE_RESIGNATION,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SHOW_UP,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SEND_LETTER,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_LESS_THAN_6_MONTH,
    GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_MORE_THAN_6_MONTH,
  ].includes(type)

  const hadAllRequiredFields = type => {
    if ([GERMANY_CONTRACT_ADDENDUM_TYPES.CHANGE_AMOUNT_HOURS].includes(type)) {
      return form.hoursPerWeek && form.hoursAffectingFrom
    }
    if ([GERMANY_CONTRACT_ADDENDUM_TYPES.CONTRACT_EXTENSION].includes(type)) {
      return form.endDate
    }
    if (
      [
        GERMANY_CONTRACT_ADDENDUM_TYPES.IMMEDIATE_RESIGNATION,
        GERMANY_CONTRACT_ADDENDUM_TYPES.FUTURE_RESIGNATION,
        GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SHOW_UP,
        GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_DOESNT_SEND_LETTER,
        GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_LESS_THAN_6_MONTH,
        GERMANY_CONTRACT_ADDENDUM_TYPES.TERMINATION_MORE_THAN_6_MONTH,
      ].includes(type)
    ) {
      return form.endDate && file
    }
  }

  const hoursToContractOptionsMap = {
    [CONTRACT_TYPES_STRING.WORKING_STUDENT]: hoursPerWeekOptions.slice(0, 5),
    [CONTRACT_TYPES_STRING.PART_TIME]: hoursPerWeekOptions,
    [CONTRACT_TYPES_STRING.SHORT_TERM]: hoursPerWeekOptions.slice(0, 2),
    [CONTRACT_TYPES_STRING.MARGINAL_PART_TIME]: hoursPerWeekOptions.slice(0, 2),
  }

  const hoursPerWeekByType = hoursToContractOptionsMap[
    contract.templateDocument.name
  ].filter(item => item.value !== Number(contract.metadata.fields.hoursPerWeek))

  const onHoursAffectFromChange = value => {
    handleFormChange('hoursAffectingFrom', value)
    const startsMonth = getStartsOfCurrentAndNextMonth(contract)

    if (value == 1) {
      handleFormChange('hoursAffectingFromDate', startsMonth.current)
    } else {
      handleFormChange('hoursAffectingFromDate', startsMonth.next)
    }
  }

  const options = useMemo(() => {
    return contract.status === GERMANY_CONTRACT_STATUSES.STATUS_TERMINATED
      ? addendumTypeOptions.filter(option =>
          [
            GERMANY_CONTRACT_ADDENDUM_TYPES.IMMEDIATE_RESIGNATION,
            GERMANY_CONTRACT_ADDENDUM_TYPES.FUTURE_RESIGNATION,
          ].includes(option.value),
        )
      : addendumTypeOptions
  }, [contract.status])

  const renderElements = () => {
    if (viewType !== 'edit') return []
    const TypeSelector = () => {
      return (
        <div className={classes.bodyContainer}>
          <div className={classes.selectTitle}>Addendum type:</div>
          <div>
            <div className={classes.select}>
              <Select
                disableUnderline
                value={type}
                onChange={event => handleTypeChange(event.target.value)}
                style={{
                  width: 270,
                  height: 36,
                  border: 'none',
                  paddingRight: 6,
                }}
              >
                {options.map(option => (
                  <MenuItem
                    key={option.label}
                    value={option.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 16,
                      fontFamily: "'Proxima Nova', sans-serif",
                      backgroundColor:
                        type == option.value ? '#E2E2FC' : 'white',
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>
      )
    }

    let elements = [<TypeSelector />]

    const HoursSelector = () => {
      return (
        <div className={classes.bodyContainer}>
          <div className={classes.selectTitle}>Amount of hours per week:</div>
          <div>
            <div className={classes.select}>
              <Select
                disableUnderline
                value={form.hoursPerWeek}
                onChange={event =>
                  handleFormChange('hoursPerWeek', event.target.value)
                }
                style={{
                  width: 270,
                  height: 36,
                  border: 'none',
                  paddingRight: 6,
                }}
              >
                {hoursPerWeekByType.map(option => (
                  <MenuItem
                    key={option.label}
                    value={option.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 16,
                      fontFamily: "'Proxima Nova', sans-serif",
                      backgroundColor:
                        form.hoursPerWeek == option.value ? '#E2E2FC' : 'white',
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>
      )
    }

    const EndDateSelector = () => {
      return (
        <div
          style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <div className={classes.bodyContainer}>
            <div className={classes.selectTitle}>Specify the end date:</div>
            <div className={classes.datePicker}>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                <KeyboardDatePicker
                  disableToolbar
                  inputProps={{ readOnly: true }}
                  minDate={
                    isDatesInRangeContract ? contract.startAt : undefined
                  }
                  maxDate={isDatesInRangeContract ? contract.endAt : undefined}
                  helperText={null}
                  disabled={!isEndDateEnabled}
                  variant="dialog"
                  format="DD-MM-YYYY"
                  margin="normal"
                  id="date-picker-inline"
                  value={form.endDate}
                  onChange={date => {
                    handleFormChange('endDate', date)
                    validateEndDate({
                      endDate: date,
                      contract,
                      type,
                      setErrors: setValidationErrors,
                      isDatesInRangeContract,
                    })
                  }}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  className={classes.inputRoot}
                />
              </MuiPickersUtilsProvider>
            </div>
          </div>
          {validationErrors.endDate && (
            <div className={classes.error}>{validationErrors.endDate}</div>
          )}
        </div>
      )
    }

    const HoursAffectFromSelector = () => {
      return (
        <div className={classes.bodyContainer}>
          <div className={classes.selectTitle}>
            The change takes effect from:
          </div>
          <div>
            <div className={classes.select}>
              <Select
                disableUnderline
                value={form.hoursAffectingFrom}
                onChange={event => onHoursAffectFromChange(event.target.value)}
                style={{
                  width: 270,
                  height: 36,
                  border: 'none',
                  paddingRight: 6,
                }}
              >
                {hoursChangeAffectingOptionsFormatted.map(option => (
                  <MenuItem
                    key={option.label}
                    value={option.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 16,
                      fontFamily: "'Proxima Nova', sans-serif",
                      backgroundColor:
                        form.hoursAffectingFrom == option.value
                          ? '#E2E2FC'
                          : 'white',
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
        </div>
      )
    }
    if (itsHoursChangeFlow) {
      elements.push(<HoursSelector />)
      elements.push(<HoursAffectFromSelector />)
    }
    if (itsEndTimeChangingFlow) {
      elements.push(<EndDateSelector />)
    }
    return elements.map(el => el)
  }

  const hoursChangeAffectingOptions = [
    { label: '1st of the current month', value: 1 },
    { label: '1st of the next month', value: 2 },
  ]

  // Removed '1st of the next month' options if contract has end date in current month
  const hoursChangeAffectingOptionsFormatted = moment(
    contract.endAt,
  ).isSameOrBefore(moment().endOf('month'), 'day')
    ? hoursChangeAffectingOptions.slice(0, 1)
    : hoursChangeAffectingOptions

  const hasValidationError =
    validationErrors.endDate !== undefined && validationErrors.endDate !== null

  const generateButtons = () => {
    const viewTypeButtonsMap = {
      edit: {
        okayTitle: canShowAttachmentBlock
          ? 'Save and update the contract'
          : 'Send for signing',
        closeTitle: 'Cancel',
        onOkay: () => setViewType('confirm'),
        onClose: () => setViewType('cancel'),
        okayDisabled: hasValidationError
          ? hasValidationError
          : !hadAllRequiredFields(type),
      },
      confirm: {
        okayTitle: 'Confirm',
        closeTitle: 'Edit addendum',
        onOkay: async () => {
          try {
            setIsLoading(true)
            await onCreate({
              parentUserContractId: contract.id,
              name: type,
              startAt: form.hoursAffectingFromDate,
              endAt: form.endDate ? form.endDate.format('YYYY-MM-DD') : null,
              hoursPerWeek: form.hoursPerWeek,
              file: file ? await getBase64(file[0]) : null,
            })
            onCancel()
            refetchAmbassadorData()
          } catch (error) {
            onCancel()
          }
        },
        onClose: () => setViewType('edit'),
        okayDisabled: isLoading,
      },
      cancel: {
        okayTitle: 'Confirm cancelation',
        closeTitle: 'Discard',
        onOkay: () => onCancel(),
        onClose: () => setViewType('edit'),
        okayDisabled: false,
      },
    }
    const buttonsProps = viewTypeButtonsMap[viewType]
    return (
      <div className={classes.buttonContainer}>
        <GenericFlatButton
          type={'outline'}
          title={buttonsProps.closeTitle}
          onClick={buttonsProps.onClose}
          disabled={isLoading}
        />
        <GenericFlatButton
          type={'filled'}
          title={buttonsProps.okayTitle}
          onClick={buttonsProps.onOkay}
          loading={isLoading}
          disabled={buttonsProps.okayDisabled}
        />
      </div>
    )
  }

  const headerDataMap = {
    edit: {
      title: `Create addendum for the ${contractName}`,
      subtitle:
        'Please, specify the addendum type you want to add for the general contract',
    },
    confirm: {
      title: 'Confirmation',
      subtitle: `Please confirm if the details you've entered are accurate and the addendum is prepared for signing or saving.`,
      content: {
        default: [
          `Current contract end date: ${moment(contract.endAt).format(
            'DD-MM-YYYY',
          )}`,
          `New contract end date: ${moment(form.endDate).format(
            'DD-MM-YYYY',
          )} `,
        ],
        [GERMANY_CONTRACT_ADDENDUM_TYPES.CHANGE_AMOUNT_HOURS]: [
          `New working hours: ${form.hoursPerWeek}`,
          `Hours affecting from: ${form.hoursAffectingFromDate} `,
        ],
        [GERMANY_CONTRACT_ADDENDUM_TYPES.FUTURE_RESIGNATION]: [
          `Current contract end date: ${moment(contract.endAt).format(
            'DD-MM-YYYY',
          )}`,
          `New contract end date: ${moment(form.endDate).format(
            'DD-MM-YYYY',
          )} `,
        ],
      },
    },
    cancel: {
      icon: <TriangleAlert />,
      title: 'Are you sure you want to cancel addendum creation?',
      subtitle: '',
    },
  }

  const generateConfirmContent = () => {
    const content = headerDataMap.confirm.content[type]
      ? headerDataMap.confirm.content[type]
      : headerDataMap.confirm.content.default

    return content.map(item => <div className={classes.subTitle}>{item}</div>)
  }

  return (
    <div className={classes.container}>
      {headerDataMap[viewType].icon && (
        <div>{headerDataMap[viewType].icon}</div>
      )}
      <div className={classes.title}>{headerDataMap[viewType].title}</div>
      {headerDataMap[viewType].subtitle ? (
        <div className={classes.subTitle}>
          {headerDataMap[viewType].subtitle}
        </div>
      ) : null}

      {viewType === 'confirm' ? generateConfirmContent() : null}

      {renderElements()}
      {canShowAttachmentBlock && (
        <div
          key={`dropzone-${type}`}
          style={{ width: '100%', display: viewType == 'edit' ? '' : 'none' }}
        >
          <DropZoneArea
            acceptedFormats={'.pdf, image/*'}
            hint={'Click or move the document file here to upload'}
            errorMessage={
              'You are trying to upload an inappropriate file format, please select a .docs or .pdf file to upload'
            }
            setFile={setFile}
            onFileUpload={file => {
              setFile(file)
              setFileError(null)
            }}
            setError={setFileError}
          />
          <div className={classes.dropzoneError}>{fileError}</div>
        </div>
      )}
      {generateButtons()}
    </div>
  )
}
export default injectStyles(styles)(CreateAddendumModal)
